import { useCallback, useRef, useState } from "react";
import { type ApiError, isAbortError } from "@/lib/api/errors";
import { type ReconcileResponse, reconcile } from "@/lib/api/extract";
import { useLatest } from "@/lib/hooks/useLatest";

type ReconcileFilters = {
  bl: string;
};

type UseReconcileOptions = {
  onUnauthorized: (message?: string) => void;
};

type UseReconcileResult = {
  data: ReconcileResponse | null;
  filters: ReconcileFilters;
  loading: boolean;
  errorMessage: string | null;
  hasLoaded: boolean;
  setFilters: (updater: (prev: ReconcileFilters) => ReconcileFilters) => void;
  refresh: (override?: Partial<ReconcileFilters>) => Promise<void>;
};

const DEFAULT_FILTERS: ReconcileFilters = {
  bl: "",
};

function areFiltersEqual(
  left: ReconcileFilters,
  right: ReconcileFilters,
): boolean {
  return left.bl === right.bl;
}

export function useReconcile({
  onUnauthorized,
}: UseReconcileOptions): UseReconcileResult {
  const [data, setData] = useState<ReconcileResponse | null>(null);
  const [filters, setFiltersState] =
    useState<ReconcileFilters>(DEFAULT_FILTERS);
  const [loading, setLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const [hasLoaded, setHasLoaded] = useState(false);
  const filtersRef = useRef(filters);
  const onUnauthorizedRef = useLatest(onUnauthorized);
  const refreshInFlightRef = useRef<Promise<void> | null>(null);
  const refreshAbortRef = useRef<AbortController | null>(null);

  const setFilters = useCallback(
    (updater: (prev: ReconcileFilters) => ReconcileFilters) => {
      setFiltersState((prev) => {
        const next = updater(prev);
        filtersRef.current = next;
        return next;
      });
    },
    [],
  );

  const refresh = useCallback(
    async (override: Partial<ReconcileFilters> = {}) => {
      if (refreshInFlightRef.current) {
        return refreshInFlightRef.current;
      }

      const nextFilters = { ...filtersRef.current, ...override };

      if (!areFiltersEqual(filtersRef.current, nextFilters)) {
        filtersRef.current = nextFilters;
        setFiltersState(nextFilters);
      }

      const abortController = new AbortController();
      refreshAbortRef.current?.abort();
      refreshAbortRef.current = abortController;

      const request = (async () => {
        try {
          setLoading(true);
          const response = await reconcile({
            bl: nextFilters.bl.trim() || null,
            sourceDocumentSha256: null,
            onlyOpen: false,
            strictExpectedOnly: true,
            signal: abortController.signal,
          });
          setData(response);
          setErrorMessage(null);
        } catch (error) {
          if (isAbortError(error)) {
            return;
          }

          const apiError = error as ApiError;
          if (apiError.status === 401) {
            onUnauthorizedRef.current();
            return;
          }
          setErrorMessage(
            apiError.message ??
              "Impossibile recuperare la verifica fatture-polizze.",
          );
        } finally {
          if (refreshAbortRef.current === abortController) {
            refreshAbortRef.current = null;
          }
          setHasLoaded(true);
          setLoading(false);
        }
      })();

      refreshInFlightRef.current = request;

      try {
        await request;
      } finally {
        if (refreshInFlightRef.current === request) {
          refreshInFlightRef.current = null;
        }
      }
    },
    [onUnauthorizedRef],
  );

  return {
    data,
    filters,
    loading,
    errorMessage,
    hasLoaded,
    setFilters,
    refresh,
  };
}
